import type { CityDto, SourceMessageDto } from '@upme/contracts';
import { Badge } from '@upme/ui/components/badge';
import { ExternalLink, Play } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { formatPostedAt, IMPORT_STATUS_VARIANT } from '../lib/format';

interface Props {
  message: SourceMessageDto;
  city: CityDto | undefined;
}

const importedClass = 'border-emerald-500 text-emerald-700 dark:text-emerald-400';

export function RawPostPreview({ message, city }: Props) {
  const { t, i18n } = useTranslation('import-queue');
  const tz = city?.timezone ?? 'UTC';

  return (
    <div className="space-y-4 rounded-md border bg-card p-4">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <a
          href={`https://t.me/${message.channel.slug}`}
          target="_blank"
          rel="noopener noreferrer"
          className="font-mono text-xs hover:underline"
        >
          @{message.channel.slug}
        </a>
        {message.authorUsername && (
          <a
            href={`https://t.me/${message.authorUsername}`}
            target="_blank"
            rel="noopener noreferrer"
            className="font-mono text-xs text-muted-foreground hover:underline"
            title={t('rawPost.authorTitle')}
          >
            @{message.authorUsername}
          </a>
        )}
        {city && <Badge variant="outline">{city.name}</Badge>}
        <span className="tabular-nums text-muted-foreground">
          {formatPostedAt(message.postedAt, tz, i18n.language)}
        </span>
        <Badge
          variant={IMPORT_STATUS_VARIANT[message.status]}
          className={message.status === 'IMPORTED' ? importedClass : undefined}
        >
          {t(`status.${message.status}`)}
        </Badge>
      </div>

      <div>
        <a
          href={message.postUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
        >
          <ExternalLink className="h-3.5 w-3.5" />
          {t('rawPost.openInTelegram')}
        </a>
      </div>

      <div>
        <div className="text-xs text-muted-foreground mb-1">{t('rawPost.postTextLabel')}</div>
        <div className="whitespace-pre-wrap rounded-md border bg-muted/40 p-3 text-sm max-h-[60vh] overflow-y-auto">
          {message.rawText || <span className="text-muted-foreground italic">{t('rawPost.empty')}</span>}
        </div>
      </div>

      {message.mediaUrls.length > 0 && (
        <div>
          <div className="text-xs text-muted-foreground mb-2">
            {t('rawPost.mediaLabel', { count: message.mediaUrls.length })}
          </div>
          <div className="flex flex-wrap gap-2">
            {message.mediaUrls.map((url) => (
              <a
                key={url}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="block h-20 w-20 overflow-hidden rounded-md border hover:opacity-80"
              >
                <img
                  src={url}
                  alt=""
                  className="h-full w-full object-cover"
                  onError={(e) => {
                    (e.currentTarget as HTMLImageElement).style.display = 'none';
                  }}
                />
              </a>
            ))}
          </div>
        </div>
      )}

      {message.videoPreviewUrl && (
        <div>
          <div className="text-xs text-muted-foreground mb-2">{t('rawPost.videoLabel')}</div>
          <a
            href={message.videoPreviewUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="relative inline-block h-20 w-20 overflow-hidden rounded-md border hover:opacity-80"
          >
            <img
              src={message.videoPreviewUrl}
              alt=""
              className="h-full w-full object-cover"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = 'none';
              }}
            />
            <span className="absolute inset-0 flex items-center justify-center bg-black/30">
              <Play className="h-6 w-6 text-white" fill="white" />
            </span>
          </a>
        </div>
      )}
    </div>
  );
}
